import './QaResults.scss';
import cn from 'classnames';
import { parameterize } from 'inflected';
import { Fragment, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@Components/Button/Button';
import { Design, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { BPMTable, Header } from '@Components/StickyTable';
import { RunSelectionBanner } from '../../components/RunSelectionBanner';
import { DataQaGroup, DataQaResults } from '../../dataQaContract';
import { DataQaConfig } from '../../forecasting-constants';
import { scopeQaGroups } from '../../forecasting-utils';
import { DataQaRun, fetchDataQaSeries } from '../../useForecastDataQa';
import { readiness, READINESS_RANK, readinessTooltip, tacticLabel } from '../data-qa-copy';
import { SeriesState, statusText } from '../data-qa-utils';
import { classifyPath, countPathClasses, isScalingReady, PathClass } from '../qa-summary';
import { DataQaDetailModal } from './DataQaDetailModal';
import { PlatformLogo } from './PlatformLogo';
import { ReadinessCell, StatusChip } from './StatusChip';

const PATH_FILTERS = [
  'All',
  'Viable',
  'Forecast-ready',
  'Via rollup',
  'Scaling only',
  'No path',
  'Scaling-ready'
];

// Show-filter key for each partition class. The tiles use the same keys, so
// clicking a tile and picking its filter isolate the same rows.
const CLASS_FILTER: Record<PathClass, string> = {
  finest: 'Forecast-ready',
  rollup: 'Via rollup',
  scalingOnly: 'Scaling only',
  none: 'No path'
};

const tileTestId = (filterKey: string) => `dq-tile-${parameterize(filterKey)}`;

const renderNoRows = () => (
  <p className='dq-chart-caption'>No combinations match the current filters.</p>
);

export const QaResults = ({
  qa,
  s3Key,
  runs,
  onSelectRun,
  onRefresh,
  isRefreshing,
  savedScope
}: {
  qa: DataQaResults;
  s3Key: string;
  runs: DataQaRun[];
  onSelectRun: (s3Key: string) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  savedScope?: DataQaConfig;
}) => {
  // Scope the run to the channels/KPIs chosen at onboarding.
  const {
    channels: scopeChannels,
    kpis: scopeKpis,
    groups
  } = useMemo(() => scopeQaGroups(qa.groups, savedScope), [qa, savedScope]);

  // Platforms present in the run, per scoped channel (for the scope header).
  const platformsByChannel = useMemo(() => {
    const m: Record<string, string[]> = {};
    groups.forEach(g => {
      if (!m[g.channel]) {
        m[g.channel] = [];
      }
      if (!m[g.channel].includes(g.platform)) {
        m[g.channel].push(g.platform);
      }
    });
    return m;
  }, [groups]);

  return (
    <>
      <p className='dq-subtitle'>
        Every combination in scope has been QA&rsquo;d. This is a read-only view of what&rsquo;s
        viable: click any row for what it means and what to do. Selection happens on the Request
        Forecast page. Just started a new run? You&rsquo;ll get an email when it&rsquo;s done;
        refresh this page then.
      </p>

      <div className='dq-scope'>
        <div className='dq-scope-rows'>
          <div className='dq-scope-row'>
            <span className='dq-scope-label'>Channels</span>
            {scopeChannels.map(c => (
              <span key={c} className='dq-tag dq-tag-channel'>
                {c}
                {(platformsByChannel[c] ?? []).map(p => (
                  <PlatformLogo key={p} platform={p} />
                ))}
              </span>
            ))}
          </div>
          <div className='dq-scope-row'>
            <span className='dq-scope-label'>KPIs</span>
            {scopeKpis.map(k => (
              <span key={k} className='dq-tag'>
                {k}
              </span>
            ))}
          </div>
        </div>
        <RunSelectionBanner
          runs={runs}
          selectedS3Key={s3Key}
          dateRange={qa.date_range}
          combinationsCount={groups.length}
          onSelectRun={onSelectRun}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </div>

      {/* Keyed to reset the per-run filters and series file on a switch. The
          banner above stays mounted, so picking a run doesn't drop focus. */}
      <QaRunView key={s3Key} qa={qa} s3Key={s3Key} groups={groups} scopeKpis={scopeKpis} />
    </>
  );
};

interface QaRunViewProps {
  qa: DataQaResults;
  s3Key: string;
  groups: DataQaGroup[];
  scopeKpis: string[];
}

const QaRunView = ({ qa, s3Key, groups, scopeKpis }: QaRunViewProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [kpiFilter, setKpiFilter] = useState('All');
  const [pathFilter, setPathFilter] = useState('All');
  const [series, setSeries] = useState<SeriesState>({ status: 'idle' });

  // The series file covers every group, so it's fetched once — on the first
  // drill-down open, not on page load (it's ~MBs for big runs). A failed
  // fetch retries on the next drill-down open; the ref latches in-flight
  // requests since state updates are async.
  const seriesInFlight = useRef(false);
  const ensureSeries = useCallback(() => {
    if (seriesInFlight.current || series.status === 'ready' || !qa.series_file) {
      return;
    }
    seriesInFlight.current = true;
    setSeries({ status: 'loading' });
    fetchDataQaSeries(s3Key, qa.series_file)
      .then(data => setSeries({ status: 'ready', data }))
      .catch(() => setSeries({ status: 'error' }))
      .finally(() => {
        seriesInFlight.current = false;
      });
  }, [series.status, qa.series_file, s3Key]);

  const filtered = useMemo(
    () =>
      groups.filter(g => {
        if (kpiFilter !== 'All' && g.kpi !== kpiFilter) {
          return false;
        }
        switch (pathFilter) {
          case 'All':
            return true;
          case 'Viable':
            return classifyPath(g) !== 'none';
          case 'Scaling-ready':
            return isScalingReady(g);
          default:
            return CLASS_FILTER[classifyPath(g)] === pathFilter;
        }
      }),
    [groups, kpiFilter, pathFilter]
  );

  // The partition tiles sum to groups.length; each group lands in exactly one.
  const summaryTiles = useMemo(() => {
    const counts = countPathClasses(groups);
    return [
      {
        filterKey: CLASS_FILTER.finest,
        num: counts.finest,
        label: 'Forecast-ready at full granularity',
        tone: 'pass'
      },
      {
        filterKey: CLASS_FILTER.rollup,
        num: counts.rollup,
        label: 'Forecastable via rollup',
        tone: 'rollup'
      },
      {
        filterKey: CLASS_FILTER.scalingOnly,
        num: counts.scalingOnly,
        label: 'Scaling only (no forecast path)',
        tone: 'scaling'
      },
      {
        filterKey: CLASS_FILTER.none,
        num: counts.none,
        label: 'No viable path yet',
        tone: 'none'
      },
      // Not a slice of the partition: counts every group with any scaling
      // path, so it overlaps the tiles before it. Rendered after a divider.
      {
        filterKey: 'Scaling-ready',
        num: groups.filter(isScalingReady).length,
        label: 'Scaling-analysis ready (overlaps the tiles to the left)',
        tone: 'overlap'
      }
    ];
  }, [groups]);

  const openDetail = useCallback(
    (g: DataQaGroup) => {
      setSelectedId(g.id);
      ensureSeries();
    },
    [ensureSeries]
  );

  const selectedGroup = useMemo(
    () => groups.find(g => g.id === selectedId) ?? null,
    [groups, selectedId]
  );

  // Compute each group's Readiness once. The table reads it both in the cell
  // renderer and the sort key, and BPMTable may invoke the sort key per
  // comparison; recomputing it there would re-walk g.warnings every time.
  const readinessByGroup = useMemo(
    () =>
      new Map(
        groups.map(g => {
          const level = readiness(g);
          return [g.id, { level, reasons: readinessTooltip(g, level) }];
        })
      ),
    [groups]
  );

  const headers: Header[] = useMemo(() => {
    const cell = (g: DataQaGroup, content: ReactNode) => (
      <InteractiveDiv
        className='dq-cell'
        data-testid='dq-row-cell'
        data-group-id={g.id}
        data-platform={g.platform}
        data-kpi={g.kpi}
        onClick={() => openDetail(g)}
      >
        {content}
      </InteractiveDiv>
    );
    return [
      {
        label: 'Channel',
        flex: 1,
        contentGetter: (g: DataQaGroup) => g.channel,
        renderer: (g: DataQaGroup) => cell(g, g.channel)
      },
      {
        label: 'Platform',
        flex: 1,
        minFlexWidth: 96,
        contentGetter: (g: DataQaGroup) => g.platform,
        renderer: (g: DataQaGroup) => cell(g, <PlatformLogo platform={g.platform} />)
      },
      {
        label: 'Tactic',
        flex: 2,
        contentGetter: (g: DataQaGroup) => tacticLabel(g.tactic),
        renderer: (g: DataQaGroup) => cell(g, tacticLabel(g.tactic))
      },
      {
        label: 'KPI',
        flex: 1,
        contentGetter: (g: DataQaGroup) => g.kpi,
        renderer: (g: DataQaGroup) => cell(g, g.kpi)
      },
      {
        label: 'Forecast',
        flex: 1,
        contentGetter: (g: DataQaGroup) =>
          statusText(
            g.status.forecast.eligible,
            g.path.forecast.recommended_grain,
            g.path.forecast.downgraded
          ),
        renderer: (g: DataQaGroup) =>
          cell(
            g,
            <StatusChip
              eligible={g.status.forecast.eligible}
              recommendedGrain={g.path.forecast.recommended_grain}
              downgraded={g.path.forecast.downgraded}
            />
          )
      },
      {
        label: 'Scaling',
        flex: 1,
        contentGetter: (g: DataQaGroup) =>
          statusText(
            g.status.scaling.eligible,
            g.path.scaling.recommended_grain,
            g.path.scaling.downgraded
          ),
        renderer: (g: DataQaGroup) =>
          cell(
            g,
            <StatusChip
              eligible={g.status.scaling.eligible}
              recommendedGrain={g.path.scaling.recommended_grain}
              downgraded={g.path.scaling.downgraded}
            />
          )
      },
      {
        label: 'Readiness',
        flex: 1.5,
        // Sorts worst-first: Not enough data, directional, check, good to go.
        contentGetter: (g: DataQaGroup) => {
          const r = readinessByGroup.get(g.id);
          return r ? READINESS_RANK[r.level] : 0;
        },
        renderer: (g: DataQaGroup) => {
          const r = readinessByGroup.get(g.id);
          return cell(g, r ? <ReadinessCell level={r.level} reasons={r.reasons} /> : null);
        }
      }
    ];
  }, [openDetail, readinessByGroup]);

  return (
    <>
      {/* Summary tiles — click to filter the table */}
      <div className='dq-tiles'>
        {summaryTiles.map(({ filterKey, num, label, tone }) => (
          <Fragment key={filterKey}>
            {tone === 'overlap' && <div className='dq-tiles-divider' aria-hidden='true' />}
            <Button
              size={Size.LG}
              data-testid={tileTestId(filterKey)}
              design={Design.NEUTRAL}
              className={cn('dq-tile', `dq-tile-${tone}`, { selected: pathFilter === filterKey })}
              onClick={() => setPathFilter(pathFilter === filterKey ? 'All' : filterKey)}
            >
              <span className='dq-tile-num'>{num}</span>
              <span className='dq-tile-label'>{label}</span>
            </Button>
          </Fragment>
        ))}
      </div>

      <div className='dq-toolbar'>
        <span className='dq-toolbar-label'>KPI</span>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          label='All'
          value={kpiFilter}
          options={['All', ...scopeKpis]}
          onChange={(v: string) => setKpiFilter(v)}
        />
        <span className='dq-toolbar-label'>Show</span>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          label='All'
          value={pathFilter}
          options={PATH_FILTERS}
          onChange={(v: string) => setPathFilter(v)}
        />
      </div>

      <div className='dq-table-wrap'>
        <BPMTable
          className='dq-table'
          headers={headers}
          data={filtered}
          rowHeight={44}
          rowHoverClass='dq-row-hover'
          filterBar={false}
          noRowsRenderer={renderNoRows}
        />
      </div>

      <DataQaDetailModal
        group={selectedGroup}
        thresholds={qa.thresholds}
        series={series}
        hasSeriesFile={Boolean(qa.series_file)}
        onHide={() => setSelectedId(null)}
      />
    </>
  );
};
